/*
 * D-08 Student Report Improvements
 *
 * Creates student-facing report insights from the D-19 report-cluster output.
 *
 * Important:
 * - Existing survey scoring is not changed.
 * - Cluster scoring is not changed.
 * - Mixed-valence measures are not automatically classified as strengths
 *   or development areas.
 * - The generated insights are descriptive guidance, not population norms.
 */

const DEVELOPMENT_MEASURES = new Set([
  "Motivation",
  "Entrepreneurial Passion",
  "Need for Achievement",
  "Curiosity",
  "Proactivity",
  "Entrepreneurial Self-Efficacy",
  "Grit",
  "Tolerance for Ambiguity",
  "Growth Mindset",
  "Resilience",
  "Cognitive Flexibility",
  "Empathy",
  "Social Curiosity",
  "Harmonious Passion",
]);

const ACTIONS = {
  "Motivation": {
    study: "Connect an upcoming study goal to a personally meaningful reason for completing it.",
    career: "Identify the type of work and responsibilities that most strongly motivate you.",
    entrepreneurship: "Write down the problem or opportunity that gives you the strongest reason to take entrepreneurial action.",
  },
  "Entrepreneurial Passion": {
    study: "Choose a project topic that lets you explore an entrepreneurial interest in greater depth.",
    career: "Look for roles or projects that align with the activities you find most engaging.",
    entrepreneurship: "Spend time developing an entrepreneurial activity that you find meaningful and sustainable.",
  },
  "Need for Achievement": {
    study: "Set one challenging but achievable academic target and track your progress toward it.",
    career: "Choose a measurable professional goal that stretches your current capability.",
    entrepreneurship: "Define a concrete venture milestone and a realistic timeframe for achieving it.",
  },
  "Curiosity": {
    study: "Explore one topic beyond the required course material and record what new questions it creates.",
    career: "Ask questions about unfamiliar roles, industries or problems to broaden your career options.",
    entrepreneurship: "Investigate customer problems before deciding which solution to pursue.",
  },
  "Proactivity": {
    study: "Take the first step on an assessment or learning task before the deadline pressure begins.",
    career: "Initiate a conversation, project or networking activity rather than waiting for an opportunity.",
    entrepreneurship: "Test one small action that moves an idea forward this week.",
  },
  "Entrepreneurial Self-Efficacy": {
    study: "Practise breaking a complex project into smaller tasks that you can complete confidently.",
    career: "Identify one professional task you want to become more confident performing and practise it deliberately.",
    entrepreneurship: "Choose one venture-building activity and complete a small real-world test.",
  },
  "Grit": {
    study: "Use weekly milestones to maintain progress on a longer academic task.",
    career: "Choose one longer-term professional goal and review your progress regularly.",
    entrepreneurship: "Keep a simple record of venture experiments, setbacks and the next action you will take.",
  },
  "Tolerance for Ambiguity": {
    study: "Practise working on an open-ended task before every detail or answer is known.",
    career: "Take part in a project where requirements may evolve and reflect on how you respond.",
    entrepreneurship: "Test assumptions with small experiments instead of waiting for complete certainty.",
  },
  "Growth Mindset": {
    study: "Use feedback from one assessment to define a specific skill you can improve next.",
    career: "Treat constructive feedback as input for a professional development goal.",
    entrepreneurship: "After an unsuccessful experiment, record what you learned and what you will change.",
  },
  "Resilience": {
    study: "After a difficult academic experience, identify what you can adjust before the next task.",
    career: "Reflect on a professional setback and identify one constructive next action.",
    entrepreneurship: "Review setbacks as information and decide what to retain, change or stop.",
  },
  "Cognitive Flexibility": {
    study: "Solve a problem using more than one approach before deciding which works best.",
    career: "Consider alternative explanations and solutions when dealing with an unfamiliar workplace problem.",
    entrepreneurship: "Generate multiple possible responses when customer or market assumptions change.",
  },
  "Empathy": {
    study: "Actively consider another student's perspective during teamwork or peer feedback.",
    career: "Practise understanding stakeholder needs before proposing a solution.",
    entrepreneurship: "Speak with potential users and listen carefully to their needs before refining an idea.",
  },
  "Social Curiosity": {
    study: "Ask peers about their reasoning and experiences during collaborative work.",
    career: "Use networking conversations to learn how other people approach their roles and decisions.",
    entrepreneurship: "Ask potential customers open questions about their experiences rather than immediately pitching a solution.",
  },
  "Harmonious Passion": {
    study: "Make space for interests that energise your learning while maintaining balance with other responsibilities.",
    career: "Notice which professional activities you enjoy without allowing them to crowd out other priorities.",
    entrepreneurship: "Build regular time for venture activity while protecting sustainable boundaries.",
  },
};

const getEligibleMeasures = (clusters) =>
  clusters
    .flatMap((cluster) =>
      cluster.measures.map((measure) => ({
        ...measure,
        clusterName: cluster.name,
      }))
    )
    .filter(
      (measure) =>
        DEVELOPMENT_MEASURES.has(measure.name) &&
        Number.isFinite(measure.score)
    );

export const getStudentReportInsights = (clusters) => {
  const eligible = getEligibleMeasures(clusters);

  if (eligible.length === 0) {
    return {
      profileSummary:
        "Your report brings together several areas of your entrepreneurial profile. Use the detailed results below to reflect on patterns that are most relevant to your study, career and entrepreneurial goals.",
      strengths: [],
      developmentAreas: [],
      actions: [],
    };
  }

  const descending = [...eligible].sort((a, b) => b.score - a.score);
  const ascending = [...eligible].sort((a, b) => a.score - b.score);

  const strengths = descending.slice(0, 3);

  const strengthPaths = new Set(strengths.map((measure) => measure.path));

  const developmentAreas = ascending
    .filter((measure) => !strengthPaths.has(measure.path))
    .slice(0, 3);

  const strongest = strengths[0];
  const development = developmentAreas[0];

  const profileSummary =
    strongest && development
      ? `Your results suggest a current relative strength in ${strongest.name}, within ${strongest.clusterName}. ${development.name}, within ${development.clusterName}, may be useful to explore as a development opportunity. These are relative patterns within your current results rather than comparisons with a population norm.`
      : "Your report highlights patterns across your current entrepreneurial profile. Use the cluster and measure results below as prompts for reflection and development.";

  const actionSource = developmentAreas.length > 0
    ? developmentAreas
    : strengths;

  const actions = actionSource
    .map((measure) => ({
      measure: measure.name,
      clusterName: measure.clusterName,
      ...(ACTIONS[measure.name] || {}),
    }))
    .filter(
      (action) =>
        action.study &&
        action.career &&
        action.entrepreneurship
    )
    .slice(0, 3);

  return {
    profileSummary,
    strengths,
    developmentAreas,
    actions,
  };
};
